/**
 * Lucide-compatible Phosphor icon shim.
 * Bundler/tsconfig aliases `lucide-react` -> this module so existing
 * imports keep working. Icons render Phosphor regular weight.
 */
import * as React from "react";
import type { Icon as PhIcon, IconProps as PhIconProps } from "@phosphor-icons/react";
// Import from /dist/ssr: the main entry's IconBase reads IconContext via
// createContext, which doesn't exist in the React Server Components runtime
// (breaks `next build` page-data collection for any RSC page using icons).
// The SSR icons are context-free; we pass `weight` explicitly below anyway.
import {
  Archive as PhArchive,
  ArrowClockwise as PhArrowClockwise,
  ArrowCounterClockwise as PhArrowCounterClockwise,
  ArrowDown as PhArrowDown,
  ArrowDownRight as PhArrowDownRight,
  ArrowLeft as PhArrowLeft,
  ArrowLineDown as PhArrowLineDown,
  ArrowLineUp as PhArrowLineUp,
  ArrowRight as PhArrowRight,
  ArrowSquareOut as PhArrowSquareOut,
  ArrowUp as PhArrowUp,
  ArrowUpRight as PhArrowUpRight,
  ArrowsClockwise as PhArrowsClockwise,
  ArrowsCounterClockwise as PhArrowsCounterClockwise,
  ArrowsDownUp as PhArrowsDownUp,
  ArrowsIn as PhArrowsIn,
  ArrowsLeftRight as PhArrowsLeftRight,
  ArrowsOut as PhArrowsOut,
  Bell as PhBell,
  BellRinging as PhBellRinging,
  BellSlash as PhBellSlash,
  BookOpen as PhBookOpen,
  BookmarkSimple as PhBookmarkSimple,
  Brain as PhBrain,
  Briefcase as PhBriefcase,
  Broadcast as PhBroadcast,
  Bug as PhBug,
  Buildings as PhBuildings,
  Calculator as PhCalculator,
  Calendar as PhCalendar,
  CalendarBlank as PhCalendarBlank,
  Camera as PhCamera,
  CaretDown as PhCaretDown,
  CaretLeft as PhCaretLeft,
  CaretRight as PhCaretRight,
  CaretUp as PhCaretUp,
  ChartBar as PhChartBar,
  ChartBarHorizontal as PhChartBarHorizontal,
  ChartLine as PhChartLine,
  Chat as PhChat,
  ChatCircle as PhChatCircle,
  Chats as PhChats,
  Check as PhCheck,
  CheckCircle as PhCheckCircle,
  Circle as PhCircle,
  CircleDashed as PhCircleDashed,
  CircleNotch as PhCircleNotch,
  ClipboardText as PhClipboardText,
  Clock as PhClock,
  ClockCounterClockwise as PhClockCounterClockwise,
  Cloud as PhCloud,
  Coins as PhCoins,
  Columns as PhColumns,
  Compass as PhCompass,
  Copy as PhCopy,
  CreditCard as PhCreditCard,
  CurrencyCircleDollar as PhCurrencyCircleDollar,
  CurrencyDollar as PhCurrencyDollar,
  CursorClick as PhCursorClick,
  Database as PhDatabase,
  DeviceMobile as PhDeviceMobile,
  Diamond as PhDiamond,
  DotOutline as PhDotOutline,
  DotsSixVertical as PhDotsSixVertical,
  DotsThree as PhDotsThree,
  DotsThreeVertical as PhDotsThreeVertical,
  DownloadSimple as PhDownloadSimple,
  Envelope as PhEnvelope,
  Eye as PhEye,
  EyeSlash as PhEyeSlash,
  File as PhFile,
  FileArrowUp as PhFileArrowUp,
  FileText as PhFileText,
  FilmSlate as PhFilmSlate,
  Flame as PhFlame,
  Flask as PhFlask,
  FloppyDisk as PhFloppyDisk,
  Funnel as PhFunnel,
  FunnelSimple as PhFunnelSimple,
  Gauge as PhGauge,
  Gear as PhGear,
  GearSix as PhGearSix,
  Gift as PhGift,
  Globe as PhGlobe,
  Handshake as PhHandshake,
  HardHat as PhHardHat,
  Heart as PhHeart,
  Hourglass as PhHourglass,
  House as PhHouse,
  Image as PhImage,
  Info as PhInfo,
  InstagramLogo as PhInstagramLogo,
  Key as PhKey,
  Laptop as PhLaptop,
  Lightbulb as PhLightbulb,
  Lightning as PhLightning,
  Link as PhLink,
  LinkSimple as PhLinkSimple,
  LinkedinLogo as PhLinkedinLogo,
  List as PhList,
  ListChecks as PhListChecks,
  Lock as PhLock,
  LockOpen as PhLockOpen,
  MagicWand as PhMagicWand,
  MagnifyingGlass as PhMagnifyingGlass,
  MagnifyingGlassMinus as PhMagnifyingGlassMinus,
  MagnifyingGlassPlus as PhMagnifyingGlassPlus,
  Minus as PhMinus,
  Money as PhMoney,
  Monitor as PhMonitor,
  MonitorPlay as PhMonitorPlay,
  Moon as PhMoon,
  Newspaper as PhNewspaper,
  Package as PhPackage,
  PaperPlaneTilt as PhPaperPlaneTilt,
  Pause as PhPause,
  PencilLine as PhPencilLine,
  PencilSimple as PhPencilSimple,
  Percent as PhPercent,
  Phone as PhPhone,
  PhoneOutgoing as PhPhoneOutgoing,
  Play as PhPlay,
  Plug as PhPlug,
  PlugCharging as PhPlugCharging,
  Plus as PhPlus,
  PlusCircle as PhPlusCircle,
  Prohibit as PhProhibit,
  Pulse as PhPulse,
  PuzzlePiece as PhPuzzlePiece,
  Question as PhQuestion,
  Radio as PhRadio,
  Receipt as PhReceipt,
  Robot as PhRobot,
  Rocket as PhRocket,
  Rows as PhRows,
  Scissors as PhScissors,
  ShareNetwork as PhShareNetwork,
  Shield as PhShield,
  ShieldCheck as PhShieldCheck,
  ShieldWarning as PhShieldWarning,
  Sidebar as PhSidebar,
  SidebarSimple as PhSidebarSimple,
  SignIn as PhSignIn,
  SignOut as PhSignOut,
  SkipBack as PhSkipBack,
  SkipForward as PhSkipForward,
  Sliders as PhSliders,
  SlidersHorizontal as PhSlidersHorizontal,
  Sparkle as PhSparkle,
  SpeakerHigh as PhSpeakerHigh,
  SpeakerSlash as PhSpeakerSlash,
  SquaresFour as PhSquaresFour,
  Stack as PhStack,
  Star as PhStar,
  Sun as PhSun,
  Swatches as PhSwatches,
  Table as PhTable,
  Tag as PhTag,
  Target as PhTarget,
  Terminal as PhTerminal,
  Timer as PhTimer,
  Trash as PhTrash,
  TrendDown as PhTrendDown,
  TrendUp as PhTrendUp,
  Trophy as PhTrophy,
  TwitterLogo as PhTwitterLogo,
  UploadSimple as PhUploadSimple,
  User as PhUser,
  UserCircle as PhUserCircle,
  UserCircleGear as PhUserCircleGear,
  UserMinus as PhUserMinus,
  UserPlus as PhUserPlus,
  Users as PhUsers,
  VideoCamera as PhVideoCamera,
  Wallet as PhWallet,
  Warning as PhWarning,
  WarningCircle as PhWarningCircle,
  Waves as PhWaves,
  WifiSlash as PhWifiSlash,
  Wrench as PhWrench,
  X as PhX,
  XCircle as PhXCircle,
  YoutubeLogo as PhYoutubeLogo,
} from "@phosphor-icons/react/dist/ssr";

export type LucideProps = Omit<PhIconProps, "weight" | "ref"> & {
  strokeWidth?: number | string;
  absoluteStrokeWidth?: boolean;
};

export type LucideIcon = React.ForwardRefExoticComponent<
  LucideProps & React.RefAttributes<SVGSVGElement>
>;
export type Icon = LucideIcon;

function asLucide(Ph: PhIcon): LucideIcon {
  const Comp = React.forwardRef<SVGSVGElement, LucideProps>(function QwIcon(
    { strokeWidth: _sw, absoluteStrokeWidth: _asw, ...rest },
    ref,
  ) {
    return React.createElement(Ph, { ref, weight: "regular", ...rest });
  });
  Comp.displayName = (Ph as any).displayName || "QwIcon";
  return Comp;
}

export const Activity = asLucide(PhPulse);
export const AlertCircle = asLucide(PhWarningCircle);
export const AlertTriangle = asLucide(PhWarning);
export const Archive = asLucide(PhArchive);
export const ArrowDown = asLucide(PhArrowDown);
export const ArrowDownRight = asLucide(PhArrowDownRight);
export const ArrowDownToLine = asLucide(PhArrowLineDown);
export const ArrowLeft = asLucide(PhArrowLeft);
export const ArrowLeftRight = asLucide(PhArrowsLeftRight);
export const ArrowRight = asLucide(PhArrowRight);
export const ArrowRightLeft = asLucide(PhArrowsLeftRight);
export const ArrowUp = asLucide(PhArrowUp);
export const ArrowUpDown = asLucide(PhArrowsDownUp);
export const ArrowUpFromLine = asLucide(PhArrowLineUp);
export const ArrowUpRight = asLucide(PhArrowUpRight);
export const Ban = asLucide(PhProhibit);
export const Banknote = asLucide(PhMoney);
export const BarChart2 = asLucide(PhChartBar);
export const BarChart3 = asLucide(PhChartBar);
export const BarChartHorizontal = asLucide(PhChartBarHorizontal);
export const Bell = asLucide(PhBell);
export const BellOff = asLucide(PhBellSlash);
export const BellRing = asLucide(PhBellRinging);
export const Blocks = asLucide(PhSquaresFour);
export const BookOpen = asLucide(PhBookOpen);
export const Bookmark = asLucide(PhBookmarkSimple);
export const Bot = asLucide(PhRobot);
export const Brain = asLucide(PhBrain);
export const Briefcase = asLucide(PhBriefcase);
export const Bug = asLucide(PhBug);
export const Building2 = asLucide(PhBuildings);
export const Calculator = asLucide(PhCalculator);
export const Calendar = asLucide(PhCalendarBlank);
export const CalendarClock = asLucide(PhCalendar);
export const CalendarDays = asLucide(PhCalendarBlank);
export const CalendarIcon = asLucide(PhCalendarBlank);
export const Camera = asLucide(PhCamera);
export const CandlestickChart = asLucide(PhChartLine);
export const ChartCandlestick = asLucide(PhChartLine);
export const ChartNoAxesColumn = asLucide(PhChartBar);
export const Check = asLucide(PhCheck);
export const CheckCircle = asLucide(PhCheckCircle);
export const CheckCircle2 = asLucide(PhCheckCircle);
export const ChevronDown = asLucide(PhCaretDown);
export const ChevronLeft = asLucide(PhCaretLeft);
export const ChevronRight = asLucide(PhCaretRight);
export const ChevronUp = asLucide(PhCaretUp);
export const Circle = asLucide(PhCircle);
export const CircleAlert = asLucide(PhWarningCircle);
export const CircleCheck = asLucide(PhCheckCircle);
export const CircleCheckBig = asLucide(PhCheckCircle);
export const CircleDashed = asLucide(PhCircleDashed);
export const CircleDollarSign = asLucide(PhCurrencyCircleDollar);
export const CircleDot = asLucide(PhDotOutline);
export const CircleHelp = asLucide(PhQuestion);
  export const Clapperboard = asLucide(PhFilmSlate);
export const ClipboardCheck = asLucide(PhClipboardText);
export const Clock = asLucide(PhClock);
export const Clock4 = asLucide(PhClock);
export const Cloud = asLucide(PhCloud);
export const Coins = asLucide(PhCoins);
export const Columns = asLucide(PhColumns);
export const Columns3 = asLucide(PhColumns);
export const Compass = asLucide(PhCompass);
export const Construction = asLucide(PhHardHat);
export const Copy = asLucide(PhCopy);
export const CopyIcon = asLucide(PhCopy);
export const CreditCard = asLucide(PhCreditCard);
export const Database = asLucide(PhDatabase);
export const DollarSign = asLucide(PhCurrencyDollar);
export const Download = asLucide(PhDownloadSimple);
export const Edit = asLucide(PhPencilSimple);
export const Edit2 = asLucide(PhPencilSimple);
export const ExternalLink = asLucide(PhArrowSquareOut);
export const Eye = asLucide(PhEye);
export const EyeOff = asLucide(PhEyeSlash);
export const File = asLucide(PhFile);
export const FileSpreadsheet = asLucide(PhTable);
export const FileText = asLucide(PhFileText);
export const FileUp = asLucide(PhFileArrowUp);
export const Filter = asLucide(PhFunnel);
export const Flame = asLucide(PhFlame);
export const FlaskConical = asLucide(PhFlask);
export const Gauge = asLucide(PhGauge);
export const Gem = asLucide(PhDiamond);
export const Gift = asLucide(PhGift);
export const Globe = asLucide(PhGlobe);
export const Grid3x3 = asLucide(PhSquaresFour);
export const GripVertical = asLucide(PhDotsSixVertical);
export const Handshake = asLucide(PhHandshake);
export const Heart = asLucide(PhHeart);
export const HelpCircle = asLucide(PhQuestion);
export const History = asLucide(PhClockCounterClockwise);
export const Home = asLucide(PhHouse);
export const Hourglass = asLucide(PhHourglass);
export const Image = asLucide(PhImage);
export const ImageIcon = asLucide(PhImage);
export const Info = asLucide(PhInfo);
export const Instagram = asLucide(PhInstagramLogo);
export const KeyRound = asLucide(PhKey);
export const Laptop = asLucide(PhLaptop);
export const Layers = asLucide(PhStack);
export const LayoutDashboard = asLucide(PhSquaresFour);
export const LayoutGrid = asLucide(PhSquaresFour);
export const LayoutPanelLeft = asLucide(PhSidebarSimple);
export const Lightbulb = asLucide(PhLightbulb);
export const LineChart = asLucide(PhChartLine);
export const Link = asLucide(PhLink);
export const Link2 = asLucide(PhLinkSimple);
export const Linkedin = asLucide(PhLinkedinLogo);
export const List = asLucide(PhList);
export const ListChecks = asLucide(PhListChecks);
export const ListFilter = asLucide(PhFunnelSimple);
export const Loader2 = asLucide(PhCircleNotch);
export const Lock = asLucide(PhLock);
export const LogIn = asLucide(PhSignIn);
export const LogOut = asLucide(PhSignOut);
export const Mail = asLucide(PhEnvelope);
export const Maximize2 = asLucide(PhArrowsOut);
export const Menu = asLucide(PhList);
export const MessageCircle = asLucide(PhChatCircle);
export const MessageSquare = asLucide(PhChat);
export const MessagesSquare = asLucide(PhChats);
export const Minimize2 = asLucide(PhArrowsIn);
export const Minus = asLucide(PhMinus);
export const MonitorCheck = asLucide(PhMonitor);
export const MonitorPlay = asLucide(PhMonitorPlay);
export const Moon = asLucide(PhMoon);
export const MoreHorizontal = asLucide(PhDotsThree);
export const MoreVertical = asLucide(PhDotsThreeVertical);
export const MousePointerClick = asLucide(PhCursorClick);
export const Newspaper = asLucide(PhNewspaper);
export const Package = asLucide(PhPackage);
export const PackageCheck = asLucide(PhPackage);
export const PanelLeft = asLucide(PhSidebarSimple);
export const PanelLeftClose = asLucide(PhSidebarSimple);
export const PanelRightClose = asLucide(PhSidebar);
export const Pause = asLucide(PhPause);
export const PenLine = asLucide(PhPencilLine);
export const Pencil = asLucide(PhPencilSimple);
export const Percent = asLucide(PhPercent);
export const Phone = asLucide(PhPhone);
export const PhoneOutgoing = asLucide(PhPhoneOutgoing);
export const Play = asLucide(PhPlay);
export const Plug = asLucide(PhPlug);
export const PlugZap = asLucide(PhPlugCharging);
export const Plus = asLucide(PhPlus);
export const PlusCircle = asLucide(PhPlusCircle);
export const Puzzle = asLucide(PhPuzzlePiece);
export const Radar = asLucide(PhBroadcast);
export const Radio = asLucide(PhRadio);
export const Receipt = asLucide(PhReceipt);
export const RefreshCcw = asLucide(PhArrowsCounterClockwise);
export const RefreshCw = asLucide(PhArrowsClockwise);
export const Rocket = asLucide(PhRocket);
export const RotateCcw = asLucide(PhArrowCounterClockwise);
export const RotateCw = asLucide(PhArrowClockwise);
export const Rows3 = asLucide(PhRows);
export const Save = asLucide(PhFloppyDisk);
export const Scissors = asLucide(PhScissors);
export const Search = asLucide(PhMagnifyingGlass);
export const Send = asLucide(PhPaperPlaneTilt);
export const Settings = asLucide(PhGear);
export const Settings2 = asLucide(PhGearSix);
export const Share = asLucide(PhShareNetwork);
export const Share2 = asLucide(PhShareNetwork);
export const Shield = asLucide(PhShield);
export const ShieldAlert = asLucide(PhShieldWarning);
export const ShieldCheck = asLucide(PhShieldCheck);
export const SkipBack = asLucide(PhSkipBack);
export const SkipForward = asLucide(PhSkipForward);
export const SlidersHorizontal = asLucide(PhSlidersHorizontal);
export const SlidersVertical = asLucide(PhSliders);
export const Smartphone = asLucide(PhDeviceMobile);
export const Sparkles = asLucide(PhSparkle);
export const Star = asLucide(PhStar);
export const SunMedium = asLucide(PhSun);
export const SwatchBook = asLucide(PhSwatches);
export const Tag = asLucide(PhTag);
export const Target = asLucide(PhTarget);
export const Terminal = asLucide(PhTerminal);
export const Timer = asLucide(PhTimer);
export const Trash = asLucide(PhTrash);
export const Trash2 = asLucide(PhTrash);
export const TrendingDown = asLucide(PhTrendDown);
export const TrendingUp = asLucide(PhTrendUp);
export const TriangleAlert = asLucide(PhWarning);
export const Trophy = asLucide(PhTrophy);
export const Twitter = asLucide(PhTwitterLogo);
export const Unlock = asLucide(PhLockOpen);
export const Upload = asLucide(PhUploadSimple);
export const User = asLucide(PhUser);
export const UserCog = asLucide(PhUserCircleGear);
export const UserMinus = asLucide(PhUserMinus);
export const UserPlus = asLucide(PhUserPlus);
export const UserRound = asLucide(PhUserCircle);
export const Users = asLucide(PhUsers);
export const Video = asLucide(PhVideoCamera);
export const Volume2 = asLucide(PhSpeakerHigh);
export const VolumeX = asLucide(PhSpeakerSlash);
export const Wallet = asLucide(PhWallet);
export const Wand2 = asLucide(PhMagicWand);
export const WandSparkles = asLucide(PhMagicWand);
export const Waves = asLucide(PhWaves);
export const WifiOff = asLucide(PhWifiSlash);
export const Wrench = asLucide(PhWrench);
export const X = asLucide(PhX);
export const XCircle = asLucide(PhXCircle);
export const Youtube = asLucide(PhYoutubeLogo);
export const Zap = asLucide(PhLightning);
export const ZoomIn = asLucide(PhMagnifyingGlassPlus);
export const ZoomOut = asLucide(PhMagnifyingGlassMinus);
